import { get, post } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { useMutation, useQuery } from "@tanstack/react-query";
import { z } from "zod";

interface CreateTokenRequest {
  workplaceId: string;
}

interface ShiftBoardAccessToken {
  type: "shift-board-access-token";
  id: string;
  attributes: {
    link: string;
    expiresAt?: string;
  };
}

interface TokenResponse {
  data: ShiftBoardAccessToken;
}

const createTokenRequestSchema = z.object({
  workplaceId: z.string(),
});

const shiftBoardAccessTokenSchema = z.object({
  type: z.literal("shift-board-access-token"),
  id: z.string(),
  attributes: z.object({
    link: z.string(),
    expiresAt: z.string().optional(),
  }),
});

const tokenResponseSchema = z.object({
  data: shiftBoardAccessTokenSchema,
});

export function useCreateShiftBoardAccessToken() {
  return useMutation({
    mutationFn: async (requestData: CreateTokenRequest): Promise<TokenResponse> => {
      const response = await post({
        url: `${environmentConfig.REACT_APP_BASE_API_URL}/shift-board-access-token`,
        data: requestData,
        requestSchema: createTokenRequestSchema,
        responseSchema: tokenResponseSchema,
      });
      return response.data;
    },
  });
}

export function useGetLatestShiftBoardAccessToken(
  workplaceId: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ["shift-board-access-token", workplaceId],
    queryFn: async (): Promise<TokenResponse> => {
      const response = await get({
        url: `${environmentConfig.REACT_APP_BASE_API_URL}/shift-board-access-token/${workplaceId}/latest`,
        responseSchema: tokenResponseSchema,
      });
      return response.data;
    },
    enabled: options?.enabled ?? true,
  });
}
